import type { PropertyAsset } from '../../types';

/**
 * D45 — whether a descendant's sole / tenants-in-common share of a residence, passing under the will or intestacy, is
 * "inherited" for the RNRB (IHTA 1984 s.8J(2)-(4)), read from the "How do the descendants inherit?" answer.
 *
 * Property settled on the death is inherited only through an immediate post-death interest or a disabled person's
 * interest (s.8J(4)(a)), or where it becomes s.71A or s.71D property held for B (s.8J(4)(b)). A trust until 18 or 25
 * ('minor_18_25') is s.71A / s.71D property only if it arises under the will of a deceased PARENT of the beneficiary
 * (s.71A(2)(a), s.71D(2)(a); "parent" includes a step-parent and anyone with parental responsibility immediately before
 * death, s.71H) or, for a bereaved minor, on the statutory trusts of an intestacy (s.71A(1)(a); HMRC IHTM42815 gives a
 * grandparent's intestacy as an example). A grandparent's will trust for grandchildren until 25 is neither, so the
 * option alone no longer qualifies: the origin of the trust decides, and an unanswered origin is withheld, never
 * assumed (PA-IHT-RNRB-INHERIT-001 E4).
 *
 *   'parent_will' — under the will of the person who died, who was a parent of each child who benefits → qualifies
 *   'intestacy'   — no will: statutory trusts under the intestacy rules of England and Wales (Administration of Estates
 *                    Act 1925 ss.46, 47(1)) for a child under 18 → qualifies (s.71A(1)(a))
 *   'other_will'  — under the will of someone who was not the children's parent (e.g. a grandparent) → not inherited
 *   'unsure' / null — origin not known → withheld, flagged missing
 */
export type BereavedTrustOrigin = 'parent_will' | 'intestacy' | 'other_will' | 'unsure';

type InheritanceType = PropertyAsset['descendant_inheritance_type'];
type Origin = PropertyAsset['bereaved_trust_origin'];

/** The trust until 18 or 25 was set up in a way that makes it s.71A / s.71D property. */
export const bereavedTrustOriginQualifies = (o: Origin | string | undefined): boolean =>
    o === 'parent_will' || o === 'intestacy';

/** The descendant route qualifies as inherited (s.8J): outright / bare trust, an IPDI, or a qualifying 18 / 25 trust. */
export const descendantInheritanceQualifies = (t: InheritanceType | string | undefined, o: Origin | string | undefined): boolean =>
    t === 'outright' || t === 'ipdi' || (t === 'minor_18_25' && bereavedTrustOriginQualifies(o));

/** A known route that is not inherited: a trust until 18 or 25 under the will of someone who was not the parent. */
export const isNonParentAgeTrust = (t: InheritanceType | string | undefined, o: Origin | string | undefined): boolean =>
    t === 'minor_18_25' && o === 'other_will';
